import {Dictionary, DrupalProductVariationGroup, DrupalProductVariationType} from "@/types/hygiena-types";
import {Fragment, useContext, useState} from "react";
import {IconColor, ImageIcon} from "../atoms/icon";
import classNames from "classnames";
import {DictionaryContext} from "@/context/dictionary-context";

/**
 * Renders the translated label from a variation group.
 *
 * @param {DrupalProductVariationGroup} variations - A group of variations.
 * @param {Dictionary} t - The dictionary of translations to use.
 * @return string
 */
export function getVariationLabel(variations: DrupalProductVariationGroup[]|DrupalProductVariationGroup, t: Dictionary): string {
  let variationType: DrupalProductVariationType | null = null;
  if (!Array.isArray(variations)) {
    variations = [variations];
  }
  for (const variant of variations) {
    if (variant?.variation?.type) {
      variationType = variant.variation?.type;
      break;
    }
  }
  switch (variationType) {
    case DrupalProductVariationType.Reactions:
      return t?.all?.reactions ?? '';
    case DrupalProductVariationType.Runs:
      return t?.all?.runs ?? '';
    case DrupalProductVariationType.Tests:
      return t?.all?.tests ?? '';
    case DrupalProductVariationType.Quantity:
      return t?.all?.quantity ?? '';
    case DrupalProductVariationType.Volume:
      return t?.all?.volume ?? '';
    default:
      return '';
  }
}


/**
 * Presents a variation select for a group of variations.
 *
 * @param {DrupalProductVariationGroup[]} variations - The product variation groups.
 * @param {Function} callback - The variant select callback.
 * @param {boolean} [hoverEffect] - Whether to enable the hover effect.
 * @param {string} [className] - An optional class name for the widget.
 * @param {string} [chevronColor] - An optional class for the chevron color.
 * @param {boolean} [collapse] - Will simply display all options without a selector, if set to true.
 */
export default function VariationSelect({variations, callback, hoverEffect, className, chevronColor, collapse = false}: {
  variations: DrupalProductVariationGroup[],
  callback: (variation: DrupalProductVariationGroup) => void,
  hoverEffect?: boolean,
  className?: string,
  chevronColor?: string,
  collapse?: boolean,
}) {
  const [activeVariation, setActiveVariation] = useState<DrupalProductVariationGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const t = useContext<Dictionary>(DictionaryContext);

  if (!variations.filter(variant => variant?.variation?.type)?.length) return <></>;

  /**
   * Update the active variation and product number.
   *
   * @param variation
   */
  function updateVariation(variation: DrupalProductVariationGroup) {
    setActiveVariation(variation);
    setIsOpen(false);
    callback(variation);
  }

  return (
    <>
      {collapse && (
        <>
          <label className="mr-2">{getVariationLabel(variations, t)}: {variations.filter(variation => variation?.variation?.value).map(variation => {
            return `${variation.variation?.value}`;
          }).join(' / ')}</label>
        </>
      )}
      {!collapse && (
        <>
          <label className="mr-2">{getVariationLabel(variations, t)}:</label>
          <div className="inline-block">
            {/* 1 variation - a simple label */}
            {variations?.length === 1 ? (
              <span>{variations[0]?.variation?.value}</span>
            ) : (
              <div className={classNames(
                "font-bold text-primary cursor-pointer flex flex-col relative transition-all z-10",
                className ?? "",
                {"hover:text-complimentary": hoverEffect}
              )}
                   onMouseEnter={() => setIsHovering(true)}
                   onMouseLeave={() => {
                     setIsOpen(false);
                     setIsHovering(false)
                   }}>
                <ul>
                  {(variations[0]?.variation?.value || activeVariation?.variation?.value) && (
                    <li>
                      <button onClick={() => setIsOpen(!isOpen)} role="button">
                        {!activeVariation?.variation?.value ? variations[0]?.variation?.value : activeVariation?.variation?.value}
                        <ImageIcon type={"chevron-down"}
                                   color={(isHovering && hoverEffect) ? "white" : (chevronColor as IconColor) ?? "primary"} height={11}
                                   width={11} alt={"Expand"} className="inline ml-1"/>
                      </button>
                    </li>
                  )}
                  <li>
                    <ul className={classNames(
                      "absolute flex flex-col w-[max-content] bg-white px-6 text-black font-normal drop-shadow-md transition-all",
                      {"max-h-0 overflow-hidden": !isOpen},
                      {"max-h-32 py-2": isOpen}
                    )}>
                      {variations.map(variation => (
                        <Fragment key={`variation-${variation.variation?.value}-${variation.productNumbers.map(item => `item.number`)}`}>
                          {variation?.variation?.value && (
                            <li>
                              <button onClick={() => updateVariation(variation)} className="hover:text-primary py-0.5" role="link">
                                {variation?.variation?.value}
                              </button>
                            </li>
                          )}
                        </Fragment>
                      ))}
                    </ul>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
